import { BlockStack, Button, Card, InlineGrid, InlineStack, Modal, Text, TextField, Tooltip } from '@shopify/polaris';
import { IndexTable, Badge } from '@shopify/polaris';
import React, { ReactElement, useEffect, useState } from 'react';
import { DeleteMinor } from '@shopify/polaris-icons';
import { useMediaQuery } from '../../Utilities/useMediaQuery';
import getClass from '../../Utilities/getClass';
import { DI, DIProps } from '../../../Core';
import './AddLabel.css';

interface Props extends DIProps { }

function AddLabel(props: Props): ReactElement {
  const isMobileView = useMediaQuery('(max-width: 768px)');
  const [label, setLabel] = useState<string>('');
  const [createLabelLoader, setCreateLabelLoader] = useState<boolean>(false);
  const [allLabels, setAllLabels] = useState<any>([]);
  const [allLabelsLoader, setAllLabelsLoader] = useState<boolean>(false);

  const createLabel = () => {
    if (label) {
      if (allLabels.map((label: any) => label.name).includes(label)) {
        props.error("Can't create duplicate labels.");
      }
      else {
        setCreateLabelLoader(true);
        props.di.POST("shopifyhome/request/createLabel", { name: label })
          .then((res: any) => {
            if (res?.success) {
              setLabel('');
              getAllLabels();
              props.success(res?.message ?? "Label created successfully.");
            } else {
              props.error(res?.message ?? "Oops! Something went wrong.");
            }
            setCreateLabelLoader(false);
          });
      }
    } else {
      props.error("Label can't be empty.");
    }
  };

  const getAllLabels = () => {
    setAllLabelsLoader(true);
    props.di.GET("shopifyhome/request/getLabels", { count: true })
      .then((res: any) => {
        if (res?.success) {
          setAllLabels(res?.data);
        }
        else {
          setAllLabels([]);
          // props.error("Labels not found.");
        }
        setAllLabelsLoader(false);
      });
  };

  useEffect(() => {
    getAllLabels();
  }, []);

  return (
    <div className='custom-label-process__container'>
      <InlineGrid columns={isMobileView ? 1 : ['oneThird', 'twoThirds']} gap="400">
        <BlockStack gap="500">
          <BlockStack gap="150">
            <Text as='p' variant='bodyLg' fontWeight='semibold'>Create Labels</Text>
            <BlockStack gap="200">
              <Text as='p' variant='bodyMd'>Create up to 5 unique labels to categorize your products, making it easy to filter, sort, and assign them to templates.</Text>
              <Text as='span'>
                <Text as='span' variant='bodyMd' fontWeight='semibold'>Note:</Text>
                <Text as="span" variant='bodyMd'> You can only create 5 labels at a time.</Text>
              </Text>
            </BlockStack>
          </BlockStack>
          <InlineStack gap="200">
            {allLabels.length >= 5 ?
              <Tooltip content={"Oops, you’ve already created 5 labels! Please delete a few labels so you create more."}>
                <InlineStack gap="200">
                  <TextField maxLength={50} placeholder='Create Label' disabled={allLabels.length >= 5} value={label} onChange={(e: any) => {
                    if (e.length <= 50) {
                      setLabel(e);
                    }
                    else props.error("Can't enter more than 50 characters.")
                  }} label labelHidden autoComplete='off' />
                  <Button variant='primary' onClick={createLabel} disabled={allLabels.length >= 5} loading={createLabelLoader}>Add Label</Button>
                </InlineStack>
              </Tooltip>
              :
              <>
                <TextField maxLength={50} placeholder='Create Label' disabled={allLabels.length >= 5} value={label} onChange={(e: any) => {
                  if (e.length <= 50) {
                    setLabel(e);
                  }
                  else props.error("Can't enter more than 50 characters.")
                }} label labelHidden autoComplete='off' />
                <Button variant='primary' onClick={createLabel} disabled={allLabels.length >= 5} loading={createLabelLoader}>Add Label</Button>
              </>
            }
          </InlineStack>
        </BlockStack>
        <LabelProcessTable allLabels={allLabels} allLabelsLoader={allLabelsLoader} getAllLabels={getAllLabels} di={props.di} success={props.success} error={props.error} />
      </InlineGrid>
    </div >
  );
}

const LabelProcessTable = ({ allLabels, allLabelsLoader, getAllLabels, di, success, error }: any) => {
  const [myLabels, setMyLabels] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const isMobileView = useMediaQuery('(max-width: 768px)');
  const [deleteLabelLoader, setDeleteLabelLoader] = useState<boolean>(false);

  useEffect(() => {
    setMyLabels(allLabels.map((label: any) => ({
      id: label.id,
      labelName: label.name,
      date: label?.created_at ?? "N/A",
      productsTagged: label?.count ?? "N/A"
    })));
  }, [allLabels]);

  const handleDelete = (id: string) => {
    setSelectedLabelId(id);
    setOpen(true);
  };

  const confirmDelete = () => {
    if (selectedLabelId) {
      setDeleteLabelLoader(true);
      di.POST("shopifyhome/request/deleteLabel", { id: selectedLabelId })
        .then((res: any) => {
          if (res?.success) {
            success(res?.message ?? "Label deleted successfully.");
            getAllLabels();
          } else {
            error(res?.message ?? "Oops! Something went wrong.");
          }
          setOpen(false);
          setSelectedLabelId(null);
          setDeleteLabelLoader(false);
        });
    }
  };

  const rowMarkup = myLabels.map(({ id, labelName, date, productsTagged }: { id: string, labelName: string, date: string, productsTagged: string }, index: number) => (
    <IndexTable.Row id={id} key={id} position={index}>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">{labelName}</Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{date}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge>{productsTagged}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Button icon={DeleteMinor} onClick={() => handleDelete(id)} />
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <div className={getClass({ "custom-label-process__table": true, "custom-label-process__table-mainView": !isMobileView })}>
      <Card padding="0">
        <IndexTable
          loading={allLabelsLoader}
          itemCount={myLabels.length}
          headings={[{ title: 'Label Name' }, { title: 'Created On' }, { title: 'Products Assigned' }, { title: 'Actions' }]}
          selectable={false}
        >
          {rowMarkup}
        </IndexTable>
      </Card>
      <DeleteLabelModal open={open} setOpen={setOpen} confirmDelete={confirmDelete} deleteLabelLoader={deleteLabelLoader} />
    </div>
  );
};

const DeleteLabelModal = ({ open, setOpen, confirmDelete, deleteLabelLoader }: { open: boolean, setOpen: (e: any) => void, confirmDelete: () => void, deleteLabelLoader: boolean }) => (
  <Modal open={open} onClose={() => setOpen(false)} title="Delete Label" primaryAction={{ content: "Delete", onAction: confirmDelete, loading: deleteLabelLoader }} secondaryActions={[{ content: "Cancel", onAction: () => setOpen(false), disabled: deleteLabelLoader }]} size='small'>
    <Modal.Section>
      Are you sure you want to delete this label?
    </Modal.Section>
  </Modal>
);

export default DI(AddLabel);